import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
    Button,
    Divider,
    IconButton,
    Icons,
    Radio,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@curves/ui-base';
import { GradientColorHeader } from '@Local/Components/Dialog/';

import useCompanyApi from '@Apis/useCompanyApi';
import useMessageDialog from '@Util/hook/useMessageDialog';
import { refIsRequiredError } from '@Util/utils';
import { CLUB_COMPANY_OVERVIEW_ROUTER } from '@Router/routerPath';

/**
 * 建立一筆空白聯絡人資料
 */
const createEmptyContact = (isDefault) => ({
    contactID: 0,
    contactName: '',
    contactNumber: '',
    contactEmail: '',
    isDefault,
});

/**
 * 公司管理 新增／編輯 Dialog
 * 公司欄位(公司資料／發票資訊) ＋ 聯絡人(1:N)整批儲存
 */
const CompanyDialog = React.forwardRef((props, ref) => {
    const { open = false, onClose, sourceData: companyData, refresh } = props;
    const { postCompanyApi, putCompanyApi } = useCompanyApi();
    const setMessageDialog = useMessageDialog();
    const { enqueueSnackbar: _snackbar } = useSnackbar();
    const history = useHistory();

    const companyNameRef = useRef(null);
    const taxIDRef = useRef(null);
    const taxTitleRef = useRef(null);
    const companyNumberRef = useRef(null);
    const companyAddressRef = useRef(null);
    const postCodeRef = useRef(null);
    const companyUrlRef = useRef(null);

    const [contacts, setContacts] = useState([]);

    const {
        companyID = 0,
        companyName = '',
        taxID = '',
        taxTitle = '',
        companyNumber = '',
        companyAddress = '',
        postCode = '',
        companyUrl = '',
        companyType = '',
        isEnabled = true,
    } = companyData || {};

    const sourceContacts = companyData?.contracts ?? [];

    useEffect(() => {
        if (open) {
            setContacts(sourceContacts.length > 0 ? sourceContacts.map((contact) => ({ ...contact })) : []);
        }
        // eslint-disable-next-line
    }, [open]);

    const handleAddContact = () => {
        setContacts((prev) => [...prev, createEmptyContact(prev.length === 0)]);
    };

    const handleRemoveContact = (index) => {
        setContacts((prev) => {
            const removed = prev[index];
            const next = prev.filter((_, i) => i !== index);

            if (removed?.isDefault && next.length > 0 && !next.some((c) => c.isDefault)) {
                next[0] = { ...next[0], isDefault: true };
            }

            return next;
        });
    };

    const handleContactFieldChange = (index, field, value) => {
        setContacts((prev) => prev.map((contact, i) => (i === index ? { ...contact, [field]: value } : contact)));
    };

    const handleSetDefaultContact = (index) => {
        setContacts((prev) => prev.map((contact, i) => ({ ...contact, isDefault: i === index })));
    };

    const handleSubmit = async () => {
        const isError = refIsRequiredError(companyNameRef, taxIDRef);
        if (isError) return;

        if (contacts.some((contact) => !contact.contactName?.trim())) {
            _snackbar('聯絡人姓名為必填', { variant: 'error' });
            return;
        }
        if (contacts.length > 0 && !contacts.some((contact) => contact.isDefault)) {
            _snackbar('請指定一筆預設聯絡人', { variant: 'error' });
            return;
        }

        const params = {
            companyID,
            companyName: companyNameRef.current.getResult(),
            taxID: taxIDRef.current.getResult(),
            taxTitle: taxTitleRef.current.getResult(),
            companyNumber: companyNumberRef.current.getResult(),
            companyAddress: companyAddressRef.current.getResult(),
            postCode: postCodeRef.current.getResult(),
            companyUrl: companyUrlRef.current.getResult(),
            // 公司類型/是否啟用目前無實際作用，已從表單移除；沿用來源值(新增預設空字串/啟用，編輯保留原值)，避免誤停用
            companyType: companyType,
            isEnabled: isEnabled,
            contracts: contacts.map((contact) => ({
                contactID: contact.contactID || 0,
                contactName: contact.contactName,
                contactNumber: contact.contactNumber,
                contactEmail: contact.contactEmail,
                isDefault: !!contact.isDefault,
            })),
        };

        const resp = companyID ? await putCompanyApi(companyID, params) : await postCompanyApi(params);

        if (resp === undefined || resp === null || resp === false) return;

        if (typeof refresh === 'function') refresh();
        if (typeof onClose === 'function') onClose();

        if (!companyID) {
            const newCompanyID = resp;

            setMessageDialog({
                open: true,
                title: '新增公司成功',
                msg: '公司已建立，是否立即新增店舖？',
                buttonText: '前往新增',
                onConfirm: () =>
                    history.push(CLUB_COMPANY_OVERVIEW_ROUTER, {
                        preselectCompany: { companyID: newCompanyID, companyName: params.companyName },
                    }),
            });
        }
    };

    return (
        <Dialog open={open} fullWidth maxWidth={'md'}>
            <GradientColorHeader onClose={onClose}>{companyID ? '編輯公司' : '新增公司'}</GradientColorHeader>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid xs={12} item>
                        <Typography variant="subtitle1" className={'mb-2'}>
                            公司資料
                        </Typography>
                    </Grid>
                    <Grid xs={6} item>
                        <TextField key={companyName} ref={companyNameRef} label={'公司名稱'} defaultValue={companyName} fullWidth required />
                    </Grid>
                    <Grid xs={6} item>
                        <TextField key={companyNumber} ref={companyNumberRef} label={'電話'} defaultValue={companyNumber} fullWidth />
                    </Grid>
					<Grid xs={4} item>
						<TextField key={postCode} ref={postCodeRef} label={'郵遞區號'} defaultValue={postCode} fullWidth />
					</Grid>
                    <Grid xs={8} item>
                        <TextField key={companyAddress} ref={companyAddressRef} label={'地址'} defaultValue={companyAddress} fullWidth />
                    </Grid>
                    <Grid xs={12} item>
                        <TextField key={companyUrl} ref={companyUrlRef} label={'公司網址'} defaultValue={companyUrl} fullWidth />
                    </Grid>
                    <Grid xs={12} item>
                        <Divider className={'my-2'} />
                        <Typography variant="subtitle1" className={'mb-2'}>
                            發票資訊
                        </Typography>
                    </Grid>
                    <Grid xs={6} item>
                        <TextField key={taxID} ref={taxIDRef} label={'統一編號'} defaultValue={taxID} fullWidth required />
                    </Grid>
                    <Grid xs={6} item>
                        <TextField key={taxTitle} ref={taxTitleRef} label={'抬頭'} defaultValue={taxTitle} fullWidth />
                    </Grid>
                    <Grid xs={12} item>
                        <Divider className={'my-2'} />
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} className={'mb-2'}>
                            <Typography variant="subtitle1">聯絡人</Typography>
                            <Button variant={'outlined'} size={'small'} color="primary" onClick={handleAddContact}>
                                新增聯絡人
                            </Button>
                        </Stack>
                        <Typography variant="body2" className={'mb-2 font-color-3'}>
                            「預設」為主要聯絡人（單選）：有聯絡人時需指定一位。
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={'bg-default'}>姓名</TableCell>
                                        <TableCell className={'bg-default'}>電話</TableCell>
                                        <TableCell className={'bg-default'}>Email</TableCell>
                                        <TableCell className={'bg-default'} align="center">預設</TableCell>
                                        <TableCell className={'bg-default'} align="center"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contacts.map((contact, index) => (
                                        <TableRow key={`${contact.contactID}_${index}`}>
                                            <TableCell>
                                                <TextField
                                                    value={contact.contactName}
                                                    onChange={(e) => handleContactFieldChange(index, 'contactName', e.target.value)}
                                                    fullWidth
                                                    required
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    value={contact.contactNumber}
                                                    onChange={(e) => handleContactFieldChange(index, 'contactNumber', e.target.value)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    value={contact.contactEmail}
                                                    onChange={(e) => handleContactFieldChange(index, 'contactEmail', e.target.value)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Radio checked={!!contact.isDefault} onChange={() => handleSetDefaultContact(index)} />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton size="small" onClick={() => handleRemoveContact(index)}>
                                                    <Icons.Trash fontSize={'small'} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {contacts.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                尚無聯絡人資料
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                    確認送出
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default CompanyDialog;
